import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { DataTable } from '../components/DataTable'
import { Button } from '../../../components/ui/Button'
import { Spinner } from '../../../components/ui/Spinner'
import { cn } from '../../../lib/utils'
import { Portal } from '../../../components/Portal'

const useHouseholds = () =>
  useQuery({
    queryKey: ['admin', 'households'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('households')
        .select(`
          id, name, created_at,
          household_members(count),
          profiles!households_created_by_profiles_fkey(email, display_name)
        `)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

const useDeleteHousehold = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('households').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'households'] }),
  })
}

const ExpandedRow = ({ household }) => {
  const { data: members } = useQuery({
    queryKey: ['admin', 'household-members', household.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('household_members')
        .select('role, profiles(email, display_name, member_color)')
        .eq('household_id', household.id)
      return data ?? []
    },
  })

  return (
    <div className="px-4 pb-4 pt-2 bg-background">
      <p className="text-xs font-semibold text-text-muted uppercase mb-2">Members</p>
      <div className="flex flex-wrap gap-2">
        {(members ?? []).map((m, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 text-xs bg-surface border border-border rounded-full px-3 py-1">
            <span className={`w-2 h-2 rounded-full bg-member-${m.profiles?.member_color ?? 'slate'}`} />
            {m.profiles?.display_name ?? m.profiles?.email ?? '?'}
            <span className="text-text-muted capitalize">· {m.role}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

export const HouseholdsPage = () => {
  const { data: households = [], isLoading } = useHouseholds()
  const deleteMutation = useDeleteHousehold()

  const [search,    setSearch]    = useState('')
  const [expanded,  setExpanded]  = useState(null)
  const [deleteId,  setDeleteId]  = useState(null)
  const [confirmName, setConfirm] = useState('')

  const filtered = households.filter((h) => {
    const q = search.toLowerCase()
    return !q || h.name?.toLowerCase().includes(q) || h.profiles?.email?.toLowerCase().includes(q)
  })

  const target = deleteId ? households.find((h) => h.id === deleteId) : null

  const columns = [
    {
      key: 'name', label: 'Household', sortable: true,
      render: (v, row) => (
        <button
          className="text-left font-medium text-text-primary hover:text-primary"
          onClick={(e) => { e.stopPropagation(); setExpanded(expanded === row.id ? null : row.id) }}
        >
          {v}
        </button>
      ),
    },
    {
      key: 'owner', label: 'Owner',
      render: (_, row) => row.profiles?.email ?? '—',
    },
    {
      key: 'member_count', label: 'Members',
      render: (_, row) => row.household_members?.[0]?.count ?? '—',
    },
    {
      key: 'created_at', label: 'Created', sortable: true,
      render: (v) => v ? new Date(v).toLocaleDateString() : '—',
    },
    {
      key: 'actions', label: '',
      render: (_, row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setDeleteId(row.id); setConfirm('') }}
          className="text-xs text-rose-500 hover:text-rose-700 font-medium"
        >
          Delete
        </button>
      ),
    },
  ]

  return (
    <div className="px-8 py-8 max-w-6xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Households</h1>
        <p className="text-sm text-text-muted mt-1">{households.length.toLocaleString()} total</p>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        emptyMessage="No households found."
        actions={
          <input
            type="search"
            placeholder="Search by name or owner email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72 rounded-base border border-border bg-surface px-4 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
          />
        }
      />

      {/* Expanded member view */}
      {expanded && (
        <div className="mt-3 rounded-base border border-border overflow-hidden">
          <ExpandedRow household={{ id: expanded }} />
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteId && target && (
        <Portal>
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-surface rounded-base shadow-card w-full max-w-sm p-6 space-y-4">
            <h3 className="text-lg font-bold text-text-primary">Delete household?</h3>
            <p className="text-sm text-text-secondary">
              This will permanently delete <strong>{target.name}</strong> and all its data.
              Type the household name to confirm.
            </p>
            <input
              type="text"
              placeholder={target.name}
              value={confirmName}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full rounded-base border border-border px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={() => setDeleteId(null)}>Cancel</Button>
              <Button
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white border-rose-600"
                disabled={confirmName !== target.name || deleteMutation.isPending}
                onClick={async () => {
                  await deleteMutation.mutateAsync(deleteId)
                  setDeleteId(null)
                }}
              >
                {deleteMutation.isPending ? <Spinner size="sm" /> : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
        </Portal>
      )}
    </div>
  )
}
